import { BusinessObjectBase } from './base';
import type { Address } from './address';
import type { ClassEnrollment } from './class-enrollment';
import type { ContactDetails } from './contact-details';
import type { EmergencyContact } from './emergency-contact';
import type { EventAvailability } from './event-availability';
import type { IdentificationDocument } from './identification-document';
import type { Interview } from './interview';
import type { LanguageAndSkills } from './language-and-skills';
import type { PersonalInformation } from './personal-information';
import type { Placement } from './placement';
import type { SkillsAndExperience } from './skills-and-experience';
import type { StaffType } from './staff-type';
import type { User } from './user';

export interface CandidateData {
  readonly id: number;
  readonly status?: string;
  readonly staffTypeId: number;
  readonly staffType?: StaffType;
  readonly placements?: Placement[];
  readonly classEnrollments?: ClassEnrollment[];
  readonly address?: Address;
  readonly personalInformation?: PersonalInformation;
  readonly identificationDocument?: IdentificationDocument;
  readonly contactDetails?: ContactDetails;
  readonly emergencyContact?: EmergencyContact;
  readonly skillsAndExperience?: SkillsAndExperience;
  readonly languageAndSkills?: LanguageAndSkills;
  readonly eventAvailability?: EventAvailability;
  readonly interviews?: Interview[];
  readonly userId: number;
  readonly user?: User;
}

export interface Candidate extends Readonly<CandidateData> {}
export class Candidate extends BusinessObjectBase {
  static readonly entityName = 'Candidate' as const;
  static readonly plural = 'candidates';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Candidate[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Candidate(data));
  }

  static async findOne(filter?: any): Promise<Candidate | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Candidate(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<Candidate> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Candidate(data);
  }

  static async create(data: Omit<CandidateData, 'id'>): Promise<Candidate> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Candidate(result);
  }

  static async updateById(
    id: number,
    data: Partial<CandidateData>,
  ): Promise<Candidate> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Candidate(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
